/**
 * @preserve yahoo lib/errorHelper
 */
import _ from 'lodash'
import { createDebug } from '@vzmi/component_ec_nodejs_util/dist/node/debug'
import { parsePayload } from '@vzmi/component_ec_nodejs_util/dist/lib/FetchDataUtil'
import confUtil from './confUtil'
import { API_ERROR_MAPPING } from '../app/constants/globalTypes'
import { SHOPPING_PRODUCTS_API_ERROR_MAPPING } from '../app/constants/shoppingProduct'
import authUtil from './authUtil'

const debug = createDebug('y:fe:lib:errorHelper')

const helperInstance = {}
helperInstance.initialized = false

/**
 * Initialize helper with env config, inejcts on server startup
 */
helperInstance.init = (envConf) => {
  if (helperInstance.initialized) {
    throw new Error('errorHelper already initialized')
  }
  if (!envConf) {
    throw new Error('invalid envConfData')
  }
  helperInstance.envConf = envConf
  helperInstance.initialized = true
}

helperInstance.evalAction = (action) => {
  return {
    hasError: helperInstance.actionContainsError(action),
    errors: helperInstance.extractErrors(action)
  }
}

helperInstance.evalMultipleActions = (actions) => {
  return _.map(actions, (action) => {
    return helperInstance.evalAction(action)
  })
}

/**
 * Determine whether if action contains error object
 */
helperInstance.actionContainsError = (action) => {
  if (!action) {
    return false
  }
  const errorData = action.payload && action.payload.errorData
  return !!(action.error && errorData && errorData.error)
}

/**
 * Extract error data from action
 */
helperInstance.extractErrors = (action) => {
  return _.get(action, 'errorData.error', null) || _.get(action, 'payload.errorData.error', null)
}

/**
 * Extract error result, which will be in form of `[{"invalidValue":"proposal.product.msrp: 500000","message":"[40009006] MSRP cannot be greater than CVS' limitation NT$20,000"},{"invalidValue":"proposal.listing.price: 500000","message":"[40009007] Price cannot be greater than CVS' limitation NT$20,000"}]`
 */
helperInstance.extractErrorResult = (error) => {
  // api Inactivity Timeout 時 error 為 undefined
  const result = _.get(error, 'result[0]', null)
  return result || helperInstance.transformErrors(helperInstance.extractErrors(error))
}

/**
 * Transform errors by error code to message
 */
helperInstance.transformErrors = (errors) => {
  return _.map(errors, error => {
    return {
      ...error,
      message: errorHelper.parseErrorMessage(error)
    }
  })
}

/**
 * Parsing error from error mapping
 */
helperInstance.parseErrorMessage = (error = {}) => {
  const errorCode = _.get(error, 'code')
  const mappedErrorMessage = _.get(API_ERROR_MAPPING, [errorCode], error.message)
  return mappedErrorMessage
}

/**
 * Parsing error from shopping products error mapping
 */
helperInstance.parseShoppingProductsErrorMessage = (error = {}) => {
  const runtimeError = _.get(error, 'debugError.runtimeError[0]')
  const errorCode = _.get(runtimeError, 'code')
  const mappedErrorMessage = _.get(SHOPPING_PRODUCTS_API_ERROR_MAPPING, errorCode, error.message)
  return mappedErrorMessage
}

/**
 * iso redirect method used by both server and client
 */
helperInstance.redirect = ({ url, res, timeout }) => {
  debug('ErrorHelper: Navigated to', url)
  console.warn('ErrorHelper: Navigated to', url)
  if (global.window) {
    setTimeout(() => {
      window.location.href = url
    }, timeout || 0)
  } else if (res) {
    return res.redirect(url)
  } else {
    console.warn('ErrorHelper: Missing res, unable to navigated to', url)
  }
}

/**
 * return boject with url and timeout by input error code of input error
 */
helperInstance.getRedirectObjectByError = ({ error, req, appHost, envConf }) => {
  const { isSellerPath, isOktaCookie, isSpCookie } = authUtil(req)
  if (!error || !error.code) {
    debug('getRedirectObjectByError: Please make sure input is a valid Error object.')
    // throw new Error('Please make sure input is a valid Error object.')
    console.warn('getRedirectObjectByError: Please make sure input is a valid Error object, error: ', error)
    return {}
  }
  let url
  const timeout = 0
  const localEnvConf = envConf || helperInstance.envConf || {}
  const code = Number(error.code) || error.code
  // debug('getRedirectObjectByError', code, error)
  switch (code) {
    case 40100000: // Missing or bad authentication while user login
    case 40100001: // Missing or bad authentication
    case 40100002: // Invalid cookie
    case 40100004: // Check wssid failed
    case 40100005: // Issue wssid failed
    case 40100006: // sdb cookie failed
    case 40100007: // se cookie failed
    case 40404000: { // User not found
      // APACSMDESK-19752 if sp 371 cookie failed, redirect back to ot 371 to relogin
      const detectIfFrom371 = (originalUrl) => {
        return /\/371/g.test(originalUrl)
      }
      let originalUrl
      if (req) {
        originalUrl = req.originalUrl
      } else {
        originalUrl = global.window ? global.window.location.pathname : ''
      }

      // 判斷在 /seller/* path 時當做 user 是 seller
      if (isSellerPath) {
        const urlConfKey = isOktaCookie ? 'sellerSimulateLogin' : 'sellerLogin'
        url = confUtil.getAbsUrl(localEnvConf)(urlConfKey)
        break
      }

      // 導向 SP 模擬登入頁
      const urlConfKey = detectIfFrom371(originalUrl) ? 'ot371' : 'login'
      url = confUtil.getAbsUrl(localEnvConf)(urlConfKey)

      if (isSpCookie) {
        // 有 _sp cookie -> 導向 SP 首頁
        url = confUtil.getAbsUrl(localEnvConf)('home')
      }
      break
    }
    case 4010001: // get se cookie OT okta cookie failed
    case 40100003: { // Simulated login but has no Okta cookie
      let spUrl
      if (req) {
        spUrl = `${localEnvConf.appHost}${req.originalUrl}`
      } else {
        spUrl = appHost || localEnvConf.appHost
      }
      url = confUtil.getTemplateUrl('refreshOkta', {
        dotDoneUrl: encodeURIComponent(spUrl)
      })
      break
    }
    case 95000000: // API server error
      url = confUtil.getTemplateUrl('error', {
        code: '95000000'
      })
      break
    default:
      // return {}
      break
  }
  return {
    url,
    timeout
  }
}

const errorHelper = {
  initHelper: helperInstance.init,
  evalAction: helperInstance.evalAction,
  evalMultipleActions: helperInstance.evalMultipleActions,
  actionContainsError: helperInstance.actionContainsError,
  extractErrors: helperInstance.extractErrors,
  extractErrorResult: helperInstance.extractErrorResult,
  transformErrors: helperInstance.transformErrors,
  parseErrorMessage: helperInstance.parseErrorMessage,
  parseShoppingProductsErrorMessage: helperInstance.parseShoppingProductsErrorMessage,
  redirect: helperInstance.redirect,
  refreshOkta: helperInstance.refreshOkta,
  getRedirectObjectByError: helperInstance.getRedirectObjectByError
}

export default errorHelper

export const resHandler = res => parsePayload(res)
  .catch(payload => {
    const extractedError = _.get(errorHelper.extractErrors(payload), 0)
    return Promise.reject(extractedError)
  })

export const httpHandler = res => {
  const serviceErrorMessage = _.get(res, 'payload.message')
  const isRequestError = !_.get(res, 'payload.errorData.error')
  if (res.error && isRequestError && serviceErrorMessage) {
    return Promise.reject(res.payload)
  }
  return resHandler(res)
}
